import type { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { focusVisibleMixin } from '@elfsight/widget-ui';
import {
  getHandleActionKeyDown,
  getInvertedColor
} from '@elfsight/widget-utils';

type PositionVariationType = keyof ReturnType<
  typeof generatePositionVariations
>;

interface PairSliderLabel extends PropsWithChildren {
  position: PositionVariationType;
  fontSize: number;
  compact: boolean;
  hidden: boolean;
  backgroundColor?: string;
  onClick: () => void;
  active?: boolean;
}

export function PairSliderLabel({
  position: positionVariation,
  fontSize,
  compact,
  hidden,
  children,
  onClick,
  backgroundColor = '',
  active = false
}: PairSliderLabel) {
  const buttonAccentColor = getInvertedColor(backgroundColor, [
    'rgb(255, 255, 255)',
    'rgb(0, 0, 0)'
  ]);

  if (!children) {
    return null;
  }

  return (
    <Container
      {...getPosition(positionVariation, compact)}
      role="button"
      tabIndex={0}
      onKeyDown={getHandleActionKeyDown(onClick)}
      onClick={onClick}
      _elementPadding={compact ? '4px 8px' : '6px 10px'}
      _elementBackgroundColor={backgroundColor}
      _elementFontSize={fontSize}
      _elementHidden={hidden}
      _elementActive={active}
      _accentColor={buttonAccentColor}
    >
      {children}
    </Container>
  );
}

function generatePositionVariations(offset: number) {
  return {
    'before-horizontal-start': {
      top: offset,
      left: offset,
      right: undefined,
      bottom: undefined,
      transform: undefined
    },
    'before-horizontal-center': {
      top: '50%',
      left: offset,
      transform: 'translate(0, -50%)',
      right: undefined,
      bottom: undefined
    },
    'before-horizontal-end': {
      bottom: offset,
      left: offset,
      right: undefined,
      top: undefined,
      transform: undefined
    },
    'before-vertical-start': {
      top: offset,
      left: offset,
      right: undefined,
      bottom: undefined,
      transform: undefined
    },
    'before-vertical-center': {
      top: offset,
      left: '50%',
      transform: 'translate(-50%, 0)',
      right: undefined,
      bottom: undefined
    },
    'before-vertical-end': {
      top: offset,
      right: offset,
      left: undefined,
      bottom: undefined,
      transform: undefined
    },
    'after-horizontal-start': {
      top: offset,
      right: offset,
      left: undefined,
      bottom: undefined,
      transform: undefined
    },
    'after-horizontal-center': {
      top: '50%',
      right: offset,
      transform: 'translate(0, -50%)',
      left: undefined,
      bottom: undefined
    },
    'after-horizontal-end': {
      bottom: offset,
      right: offset,
      left: undefined,
      top: undefined,
      transform: undefined
    },
    'after-vertical-start': {
      bottom: offset,
      left: offset,
      right: undefined,
      top: undefined,
      transform: undefined
    },
    'after-vertical-center': {
      bottom: offset,
      left: '50%',
      transform: 'translate(-50%, 0)',
      right: undefined,
      top: undefined
    },
    'after-vertical-end': {
      bottom: offset,
      right: offset,
      left: undefined,
      top: undefined,
      transform: undefined
    }
  };
}

function getPosition(
  positionVariation: PositionVariationType,
  compact: boolean
) {
  const offset = compact ? 8 : 12;

  const availablePositions = generatePositionVariations(offset);

  const { top, right, bottom, left, transform } =
    availablePositions[positionVariation] || {};

  return {
    _elementTop: top,
    _elementRight: right,
    _elementBottom: bottom,
    _elementLeft: left,
    _elementTransform: transform
  };
}

const Container = styled.div<{
  _elementTop?: number | string;
  _elementRight?: number | string;
  _elementBottom?: number | string;
  _elementLeft?: number | string;
  _elementTransform?: string;
  _elementPadding?: string;
  _elementBackgroundColor?: string;
  _elementFontSize?: number;
  _accentColor: string;
  _elementHidden: boolean;
  _elementActive: boolean;
}>`
  position: absolute;
  z-index: 4;
  max-width: 33%;
  border: 2px solid transparent;
  border-radius: 4px;
  box-shadow: 0 2px 6px 0 transparent;
  line-height: 1.25;
  word-wrap: break-word;
  cursor: pointer;
  transition:
    visibility 0.2s linear,
    border-color 0.2s ease,
    opacity 0.2s ease,
    box-shadow 0.4s ease;

  transition-delay: 0.1s;

  ${({ _elementHidden }) =>
    _elementHidden &&
    css`
      visibility: hidden;
      opacity: 0;
      pointer-events: none;
    `};

  ${({
    _elementTop: top,
    _elementRight: right,
    _elementBottom: bottom,
    _elementLeft: left,
    _elementTransform: transform,
    _elementPadding: padding,
    _elementBackgroundColor: backgroundColor,
    _elementFontSize: fontSize,
    _accentColor,
    _elementActive
  }) =>
    css({
      top,
      right,
      bottom,
      left,
      transform,
      padding,
      backgroundColor,
      fontSize,
      color: _accentColor,
      borderColor: _elementActive ? _accentColor : undefined
    })};

  ${focusVisibleMixin}

  &:not(:active):hover {
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.4);
  }
`;
